/* global google */
import Vue from 'vue';
import GoogleMapsPlugin from '../../plugins/GoogleMapsPlugin';
import {loaded} from '../../utils/GoogleMapsLoader';
import Config from 'sdw-config';
import EventBus from '../../utils/EventBus';
import {MarkerClusterer} from '../../vendor/MarkerClusterer';
import {InfoBox} from '../../vendor/InfoBox';

// Define Google Maps Plugin to load the API
if (typeof window.sdwConfig !== 'undefined' && typeof window.sdwConfig.google !== 'undefined') {
    Vue.use(GoogleMapsPlugin, {
        load: {
            key: window.sdwConfig.google.maps.apiKey,
            v: '3'
        }
    });
}

const defaultInfoBoxOptions = {
    disableAutoPan: false,
    maxWidth: 238,
    boxClass: 'map__info-window',
    alignBottom: true,
    closeBoxMargin: '-10px',
    closeBoxURL: Config.paths.assets + 'images/close.svg'
};

export default {
    props: {
        markers: {}, // Object with per marker an InfoWindowOptions object
        markersSrc: '',
        mapOptions: {},
        infoWindowOptions: {},
        eventData: {},
        clusters: {
            default: true,
            type: Boolean
        },
        zoomLevel: {
            default: 17,
            type: Number,
            required: false
        }
    },
    mounted() {
        return loaded.then(() => {

            // getting the DOM element where to create the map
            const element = this.$refs['map-container'];

            // Set Google Maps options
            let googleMapsOptions = Object.assign({}, {
                clickableIcons: false,
                mapTypeControl: false,
                streetViewControl: false,
                scrollwheel: false,
                maxZoom: 15
            }, this.options);

            this.markerWindows = [];
            this.markerArray = [];

            this.mapsObject = new google.maps.Map(element, googleMapsOptions);
            let loadedListener = google.maps.event.addListener(this.mapsObject, 'tilesloaded', async function () {
                const selectors = 'a, button, div[tabindex="0"]';
                var elements = element.querySelectorAll(selectors);
                //Initially there is only a div present, wait for at least one control to appear
                const numberOfElementsToWaitFor = 2;
                while (elements.length < numberOfElementsToWaitFor) {
                    await new Promise(r => setTimeout(r, 250));
                    elements = element.querySelectorAll(selectors);
                }
                [].slice.apply(elements).forEach(function (item) {
                    item.setAttribute('tabindex', '-1');
                });
                google.maps.event.removeListener(loadedListener);
            });
            this.addMarkers();

            EventBus.$on('MapLink:scrollStart', eventData => {
                // Removes previous listeners to prevent hiding of previously visited markers
                if (this.mapScrollIdleListener) {
                    google.maps.event.removeListener(this.mapScrollIdleListener);
                }

                this.closeOtherInfoWindows(eventData.markerId);

                let showInfoWindow = () => {
                    // Finds and opens map marker based on id
                    const currentMarker = this.getMarker(eventData.markerId);
                    if (!currentMarker.markerInfoBox.getVisible()) {
                        currentMarker.markerInfoBox.open(this.mapsObject, currentMarker.mapsMarker);
                    } else {
                        currentMarker.markerInfoBox.panBox_(false);
                    }
                    currentMarker.mapsMarker.setVisible(false);
                };

                showInfoWindow();

                // Deep zoom to circumvent clustering
                if (this.mapsObject.getZoom() !== this.zoomLevel) {
                    this.mapsObject.setZoom(this.zoomLevel);
                }
            });
        }).catch((error) => {
            throw error;
        });
    },
    methods: {
        getIcon(type, hover) {
            const types = ['bed', 'binoculars', 'building', 'coffee', 'cutlery', 'default', 'ferriswheel', 'lemonade',
                'mask', 'monument', 'pillar', 'ticket', 'tent'];
            const selectedType = types.indexOf(type) > -1 ? type : 'default';
            return {
                url: `${this.markersSrc}marker-${selectedType}-${hover === true ? 'hover' : 'default'}.svg`,
                scaledSize: new google.maps.Size(37, 44),
                anchor: new google.maps.Point(18, 44)
            };
        },
        addMarkers() {
            const markerBounds = new google.maps.LatLngBounds();

            for (const marker of this.markers) {
                this.addMarker(marker);

                // Add marker to bounds if needed
                markerBounds.extend(marker);
            }

            if (this.clusters) {
                this.addClusterer();
            }

            let zoomChangeBoundsListener = google.maps.event.addListener(this.mapsObject, 'bounds_changed', function () {
                this.$nextTick(function () {
                    this.mapsObject.setOptions({maxZoom: null});
                    google.maps.event.removeListener(zoomChangeBoundsListener);
                });
            }.bind(this));

            // Fit the map to all markers
            this.mapsObject.fitBounds(markerBounds);
        },
        closeOtherInfoWindows(markerId) {
            this.markerWindows.forEach(function (marker) {
                if (marker.mapsMarker.id !== markerId) {
                    if (marker.markerInfoBox.getVisible()) {
                        marker.markerInfoBox.close();
                    }
                }
            })
        },
        addMarker(marker) {
            const that = this;

            const mapsMarker = new google.maps.Marker({
                position: marker,
                icon: this.getIcon(marker.type),
                map: this.mapsObject,
                optimized: false,
                id: marker.id
            });

            this.markerArray.push(mapsMarker);

            mapsMarker.addListener('mouseover', function () {
                this.setIcon(that.getIcon(marker.type, true));
            });

            mapsMarker.addListener('mouseout', function () {
                this.setIcon(that.getIcon(marker.type, false));
            });

            // Do we need an infoWindow
            if (typeof marker.infoWindow !== 'undefined') {

                let markerInfoBoxContent = `<p class="map__info-window-title">${marker.title}</p><p class="map__info-window-content">${marker.infoWindow.content}</p>`;

                const markerInfoBoxOptions = Object.assign({}, defaultInfoBoxOptions, this.infoWindowOptions, marker.infoWindow, {
                    pixelOffset: new google.maps.Size(-124, -14),
                    infoBoxClearance: new google.maps.Size(60, 60),
                    content: markerInfoBoxContent
                });

                const markerInfoBox = new InfoBox(markerInfoBoxOptions);

                markerInfoBox.onRemove = function () {
                    mapsMarker.setVisible(true);

                    mapsMarker.setIcon(that.getIcon(marker.type, false));

                    /* This is a must because of InfoBox module issue */
                    if (this.div_) {
                        this.div_.parentNode.removeChild(this.div_);
                        this.div_ = null;
                    }
                };

                this.markerWindows.push({markerInfoBox, mapsMarker});

                mapsMarker.addListener('click', function () {
                    if (this.mapScrollIdleListener) {
                        google.maps.event.removeListener(this.mapScrollIdleListener);
                    }
                    this.closeOtherInfoWindows(mapsMarker.id);
                    markerInfoBox.open(this.mapsObject, mapsMarker);
                    mapsMarker.setVisible(false);

                    // Track event
                    const markerEventData = Object.assign({}, this.eventData, marker.eventData);
                    EventBus.$emit('Maps:open', markerEventData);
                }.bind(this));
            }
        },
        addClusterer() {
            this.markerClusterer = new MarkerClusterer(
                this.mapsObject,
                this.markerArray,
                {
                    imagePath: `${this.markersSrc}map-marker-cluster`,
                    maxZoom: 18,
                    styles: [{
                        url: `${this.markersSrc}map-marker-cluster1.svg`,
                        height: 47,
                        width: 47,
                        textColor: '#fff'
                    }]
                }
            );

            google.maps.event.addListener(this.markerClusterer, 'clusterclick', function (e) {
                const clusterEventData = Object.assign({}, this.eventData, {'markerCount': e.markers_.length});
                EventBus.$emit('Maps:cluster', clusterEventData);
            }.bind(this));

            // Event fires after clustering has finished.
            google.maps.event.addListener(this.markerClusterer, 'clusteringend', function () {
                // Get the collection of marker clusters
                const clusterObject = this.markerClusterer.getClusters();

                let clusteredMarkers = [];

                // Store an array of all markers currently in a cluster
                for (let cluster of clusterObject) {
                    if (cluster.markers_.length <= 1) {
                        continue;
                    }
                    for (let marker of cluster.markers_) {
                        clusteredMarkers.push(marker);
                    }
                }

                // Filter markerWindows on all markers currently in a cluster
                let filteredWindows = this.markerWindows.filter(function (e) {
                    for (let marker of clusteredMarkers) {
                        if (e.mapsMarker === marker) {
                            return true;
                        }
                    }
                });

                // Close all markerInfoBoxes found in the filtered markerWindows
                for (let window of filteredWindows) {
                    if (window.markerInfoBox.getVisible()) {
                        window.markerInfoBox.close();
                    }
                }

                // Hide marker for open windows
                const markers  = this.markerWindows.filter(function (marker) {
                    return marker.markerInfoBox.getVisible();
                });
                for (let marker of markers) {
                    marker.mapsMarker.setVisible(false);
                }

            }.bind(this))
        },
        getMarker(markerId) {
            return this.markerWindows.find((marker) => marker.mapsMarker.id === markerId);
        }
    }
}
